// user-hash.ts — a pseudonymous account key, so MAU counts people, not browsers.
//
// Born 2026-10-05 for pricing. The sport app's `user.Id` is hashed IN THE PAGE as
// SHA-256("<apiKey>:<id>") (first 32 hex); only the hash is sent as `user_hash`.
// ⛔ `user.Id` reads 0 while the app hydrates (adapter.ts betBuilderGate), so 0 /
// null / non-numbers are never hashed. A logout clears the cache.

export interface UserHash {
  /** Re-read the id; hash it if it changed. Call on an interval. */
  tick: () => Promise<void>;
  /** The cached hash, or null. Sync, for sendEvent. */
  current: () => string | null;
}

export function makeUserHash(apiKey: string, read: () => number | null): UserHash {
  let lastId: number | null = null;
  let hash: string | null = null;
  const tick = async (): Promise<void> => {
    let id: number | null = null;
    try { id = read(); } catch { id = null; }
    if (typeof id !== "number" || !Number.isFinite(id) || id <= 0) {
      lastId = null; hash = null; return;
    }
    if (id === lastId && hash) return;
    try {
      const buf = await crypto.subtle.digest(
        "SHA-256", new TextEncoder().encode(`${apiKey}:${id}`));
      hash = Array.from(new Uint8Array(buf))
        .map(b => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
      lastId = id;
    } catch { hash = null; lastId = null; }
  };
  return { tick, current: () => hash };
}
